/**
 * SpotMarketTab - View spot market prices and history
 */

import React from "react";
import { FuelPriceChart } from "./FuelPriceChart.jsx";
import { formatFuelPrice, formatGallons, getPriceTrend } from "../../systems/FuelSystem.js";

export function SpotMarketTab({ fuelState, macroState }) {
  const spotPrice = fuelState?.spotPrice || 2.95;
  const priceHistory = fuelState?.priceHistory || [];
  const hedges = fuelState?.hedges || [];
  const { change, trend } = getPriceTrend(fuelState || {});

  // Calculate weekly and monthly averages
  const weeklyAvg = priceHistory.length >= 7
    ? priceHistory.slice(-7).reduce((sum, d) => sum + d.price, 0) / 7
    : spotPrice;

  const monthlyAvg = priceHistory.length > 0
    ? priceHistory.reduce((sum, d) => sum + d.price, 0) / priceHistory.length
    : spotPrice;

  // Volatility indicator from macro state
  const volatility = macroState?.global?.OPVI || 0.35;
  const volatilityLevel = volatility > 0.6 ? "HIGH" : volatility > 0.35 ? "MODERATE" : "LOW";
  const volatilityColor = volatility > 0.6 ? "#ef4444" : volatility > 0.35 ? "#eab308" : "#22c55e";

  // Fuel price forecast (simple heuristic based on macro state)
  const fuelMultiplier = macroState?.jetFuelPriceMultiplier || 1.0;
  const forecast = fuelMultiplier > 1.1 ? "RISING" : fuelMultiplier < 0.9 ? "FALLING" : "STABLE";
  const forecastColor = fuelMultiplier > 1.1 ? "#ef4444" : fuelMultiplier < 0.9 ? "#22c55e" : "#eab308";

  return (
    <div>
      {/* Price Chart */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ marginBottom: "8px", color: "#737373", fontSize: "12px", fontWeight: "600" }}>
          30-DAY PRICE HISTORY
        </div>
        <FuelPriceChart
          priceHistory={priceHistory}
          currentPrice={spotPrice}
          hedges={hedges}
          height={160}
        />
      </div>

      {/* Price Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "24px" }}>
        <div style={{
          padding: "16px",
          backgroundColor: "#111",
          borderRadius: "8px",
          border: "1px solid #1a1a1a",
        }}>
          <div style={{ color: "#525252", fontSize: "11px", marginBottom: "4px" }}>TODAY</div>
          <div style={{ color: "#60ffd0", fontSize: "20px", fontWeight: "bold", fontFamily: "monospace" }}>
            ${spotPrice.toFixed(2)}
          </div>
          <div style={{
            color: trend === "up" ? "#ef4444" : trend === "down" ? "#22c55e" : "#737373",
            fontSize: "12px",
          }}>
            {trend === "up" ? "↑" : trend === "down" ? "↓" : "−"} ${Math.abs(change).toFixed(2)}
          </div>
        </div>

        <div style={{
          padding: "16px",
          backgroundColor: "#111",
          borderRadius: "8px",
          border: "1px solid #1a1a1a",
        }}>
          <div style={{ color: "#525252", fontSize: "11px", marginBottom: "4px" }}>7-DAY AVG</div>
          <div style={{ color: "#e5e5e5", fontSize: "20px", fontWeight: "bold", fontFamily: "monospace" }}>
            ${weeklyAvg.toFixed(2)}
          </div>
          <div style={{
            color: spotPrice > weeklyAvg ? "#ef4444" : spotPrice < weeklyAvg ? "#22c55e" : "#737373",
            fontSize: "12px",
          }}>
            {spotPrice > weeklyAvg ? "Above" : spotPrice < weeklyAvg ? "Below" : "At"} avg
          </div>
        </div>

        <div style={{
          padding: "16px",
          backgroundColor: "#111",
          borderRadius: "8px",
          border: "1px solid #1a1a1a",
        }}>
          <div style={{ color: "#525252", fontSize: "11px", marginBottom: "4px" }}>30-DAY AVG</div>
          <div style={{ color: "#e5e5e5", fontSize: "20px", fontWeight: "bold", fontFamily: "monospace" }}>
            ${monthlyAvg.toFixed(2)}
          </div>
          <div style={{
            color: spotPrice > monthlyAvg ? "#ef4444" : spotPrice < monthlyAvg ? "#22c55e" : "#737373",
            fontSize: "12px",
          }}>
            {spotPrice > monthlyAvg ? "Above" : spotPrice < monthlyAvg ? "Below" : "At"} avg
          </div>
        </div>
      </div>

      {/* Market Indicators */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ marginBottom: "12px", color: "#737373", fontSize: "12px", fontWeight: "600" }}>
          MARKET INDICATORS
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
          <div style={{
            padding: "16px",
            backgroundColor: "#111",
            borderRadius: "8px",
            border: "1px solid #1a1a1a",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <div>
              <div style={{ color: "#525252", fontSize: "11px" }}>VOLATILITY</div>
              <div style={{ color: "#e5e5e5", fontSize: "14px", fontWeight: "600" }}>
                {volatilityLevel}
              </div>
            </div>
            <div style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: volatilityColor,
            }} />
          </div>

          <div style={{
            padding: "16px",
            backgroundColor: "#111",
            borderRadius: "8px",
            border: "1px solid #1a1a1a",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <div>
              <div style={{ color: "#525252", fontSize: "11px" }}>FORECAST</div>
              <div style={{ color: "#e5e5e5", fontSize: "14px", fontWeight: "600" }}>
                {forecast}
              </div>
            </div>
            <div style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: forecastColor,
            }} />
          </div>
        </div>
      </div>

      {/* Spot Market Info */}
      <div style={{
        padding: "16px",
        backgroundColor: "#0a0a0a",
        borderRadius: "8px",
        border: "1px solid #1a1a1a",
      }}>
        <div style={{ color: "#737373", fontSize: "12px", fontWeight: "600", marginBottom: "8px" }}>
          ABOUT SPOT MARKET
        </div>
        <div style={{ color: "#a1a1a1", fontSize: "13px", lineHeight: "1.6" }}>
          Without fuel contracts, you pay the spot price for every flight. Prices fluctuate daily
          based on global market conditions. High volatility periods can see prices spike
          significantly. Consider supplier contracts or hedging to protect your margins.
        </div>
      </div>
    </div>
  );
}

export default SpotMarketTab;
